import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { FulFillment, Order, OrderFulfillmentStatus } from 'src/app/shop/models/order.model';

interface Status {
  title: string;
  text: string;
  icon: string;
  date?: string | Date;
  link?: {
    url: string;
    text: string;
  };
  color: 'success' | 'danger' | 'info' | 'warning' | 'muted' | 'primary';
  stepCompleted: boolean;
  fulfillments?: FulFillment[];
}

@Component({
    selector: 'app-fulfillment-status-bar',
    templateUrl: './fulfillment-status-bar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FulfillmentStatusBarComponent implements OnInit {
  @Input() order!: Order;

  private dict = new FromDictionaryPipe(this.lang);

  statuses: Status[] = [];

  detailsHidden = true;

  prefix = 'order-status';

  constructor(private lang: LanguageService) {}

  ngOnInit(): void {
    this.statuses = [
      {
        title: `${this.prefix}-label-ORDERED`,
        text: '',
        date: this.order.processedAt,
        icon: 'icon-order-processed',
        color: 'primary',
        stepCompleted: true,
      },
      this.status,
      this.tracking,
    ];
  }

  statusInProgress(code: OrderFulfillmentStatus) {
    return ['FULFILLED', 'IN_PROGRESS', 'ON_HOLD', 'PARTIALLY_FULFILLED'].includes(code);
  }

  get status(): Status {
    if (this.order.cancelReason) {
      return {
        title: `${this.prefix}-label-CANCELED`,
        text: 'shopify-OrderCancelReason-' + this.order.cancelReason,
        icon: 'icon-circle-close',
        color: 'danger',
        stepCompleted: true,
      };
    }
    return {
      title: `${this.prefix}-label-${this.statusInProgress(this.order.fulfillmentStatus) ? this.order.fulfillmentStatus : 'UNDEFINED'}`,
      text: `${this.prefix}-text-${this.statusInProgress(this.order.fulfillmentStatus) ? this.order.fulfillmentStatus : 'UNDEFINED'}`,
      icon: this.getIcon(this.order.fulfillmentStatus),
      color: !this.statusInProgress(this.order.fulfillmentStatus) ? 'muted' : this.getStatusColor(this.order.fulfillmentStatus),
      stepCompleted: this.statusInProgress(this.order.fulfillmentStatus),
    };
  }

  getStatusColor(code: OrderFulfillmentStatus) {
    switch (code) {
      case 'FULFILLED':
        return 'primary';
      case 'PARTIALLY_FULFILLED':
        return 'warning';
      case 'IN_PROGRESS':
        return 'primary';
      case 'ON_HOLD':
        return 'warning';
      default:
        return 'muted';
    }
  }

  getIcon(code: OrderFulfillmentStatus) {
    switch (code) {
      case 'PARTIALLY_FULFILLED':
        return 'icon-circle-pause';
      case 'IN_PROGRESS':
        return 'icon-clock';

      case 'ON_HOLD':
        return 'icon-clock';

      case 'FULFILLED':
        return 'icon-circle-check';

      case 'UNFULFILLED':
        return 'icon-circle-pause';

      // If no case has a match fallback to
      default:
        return 'icon-circle-check';
    }
  }

  get tracking(): Status {
    if (!this.order.successfulFulfillments.length) {
      return {
        title: `${this.prefix}-label-NO_TRACKING_INFO_AVAILABLE`,
        text: `${this.prefix}-text-NO_TRACKING_INFO_AVAILABLE`,
        icon: 'icon-order-underway',
        color: 'muted',
        stepCompleted: false,
      };
    }
    return {
      title: `${this.prefix}-label-TRACKING_INFO`,
      text: `${this.prefix}-text-TRACKING_INFO`,
      icon: 'icon-order-underway',
      color: 'primary',
      fulfillments: this.succesfulFulfillmentsWithTracking,
      stepCompleted: true,
    };
  }

  getLabel(prefix: string, label: string, fallback?: string) {
    const dictPlaceholder = prefix + label;
    if (this.dict.transform(dictPlaceholder, false)) {
      return this.dict.transform(dictPlaceholder, false);
    }
    console.warn('CMS dictionary: Label not filled for placeholder:', dictPlaceholder);
    return fallback;
  }

  lastActiveStep(step: Status) {
    const activeSteps = this.statuses.filter(a => a.stepCompleted);
    const lastActiveStep = activeSteps.slice(activeSteps.length - 1, activeSteps.length)?.[0];
    return lastActiveStep.title === step.title;
  }

  toggleStatusDetailsMobile() {
    this.detailsHidden = !this.detailsHidden;
  }

  get succesfulFulfillmentsWithTracking() {
    return this.order.successfulFulfillments.filter(a => a.trackingCompany || a.trackingInfo.length);
  }
}
